type TabStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** The tab's own storage; a getter, as reading it can throw where it is blocked */
export type GetTabStorage = () => TabStorage

export const LOGIN_PATH = '/login'

/**
 * Whether this tab had a session that could end, as the SDK reports a refusal on a first visit
 * with nothing stored too. Per tab: in the localStorage the tabs share, the first tab refused would
 * take it from every other.
 */
export const SIGNED_IN_MARKER_KEY = 'fe-inv:signedIn'

/** The PWA's login notice: its login page reads the JSON reason once (pwa loginNotice.ts) */
export const HOST_LOGIN_NOTICE_KEY = 'mw_login_notice_v1'

export type LoginNotice = 'session_ended'

/** Blocked storage must fail neither a request, a sign-out nor the way to the login page */
function bestEffort<T>(access: () => T): T | undefined {
  try {
    return access()
  } catch {
    return undefined
  }
}

/** fe-inv never signs in itself: the first token it sees stands in for the sign-in */
export function setSignedInMarker(getStorage: GetTabStorage): void {
  bestEffort(() => {
    const storage = getStorage()
    if (storage.getItem(SIGNED_IN_MARKER_KEY) === null) storage.setItem(SIGNED_IN_MARKER_KEY, '1')
  })
}

/** Clears the marker; whether it was set */
export function takeSignedInMarker(getStorage: GetTabStorage): boolean {
  const marked = bestEffort(() => {
    const storage = getStorage()
    const wasSet = storage.getItem(SIGNED_IN_MARKER_KEY) !== null
    storage.removeItem(SIGNED_IN_MARKER_KEY)
    return wasSet
  })
  return marked ?? false
}

/** Goes to the login page; with `notice`, the PWA's login page then says why */
export function goToLoginPage(
  notice?: LoginNotice,
  location: Pick<Location, 'assign'> = window.location,
  getStorage: GetTabStorage = () => globalThis.sessionStorage,
): void {
  if (notice) bestEffort(() => getStorage().setItem(HOST_LOGIN_NOTICE_KEY, JSON.stringify(notice)))
  location.assign(LOGIN_PATH)
}
